import { useCallback, useContext } from 'react';
import { EditorState, Transaction } from 'hubspot-prosemirror/pm/state';
import useDebouncedEditorStateContext from '../contexts/useDebouncedEditorStateContext';
import { EditorConfigContext } from '../contexts/EditorConfigContext';
import { getGlobalVars } from '../utils/globalVarUtil';
import { insertContent, insertLink } from '../utils/documentUtils';
import { convertToPlaintextState } from '../utils/plaintextUtils';
import type { InsertionType } from '../inserters/types';

interface UseContentInserterOptions<M = unknown> {
  insertionType: InsertionType;
  onInsertComplete?: Function;
  onInsertContent?: (metadata: M) => void;
  transformTransaction?: (tr: Transaction) => Transaction;
}

export default function useContentInserter<M = unknown>({
  insertionType,
  onInsertComplete,
  onInsertContent,
  transformTransaction,
}: UseContentInserterOptions<M>) {
  const { editorState, setEditorState } = useDebouncedEditorStateContext();
  const editorConfig = useContext(EditorConfigContext);
  const { usePlaintextMode, plaintextOptions } = getGlobalVars(editorConfig);

  return useCallback(
    ({ htmlBody, metadata }: { htmlBody: string; metadata?: M }) => {
      let newState: EditorState;

      if (insertionType === 'link') {
        let tr = insertLink({
          htmlBody,
          editorState,
          from: editorState.selection.from,
          to: editorState.selection.to,
        });
        if (transformTransaction) {
          tr = transformTransaction(tr);
        }
        newState = editorState.apply(tr);
      } else {
        newState = insertContent({
          htmlBody,
          editorState,
          from: editorState.selection.from,
          to: editorState.selection.to,
        });
      }

      setEditorState(
        usePlaintextMode
          ? convertToPlaintextState(newState, plaintextOptions)
          : newState
      );
      onInsertComplete?.();
      if (metadata !== undefined) {
        onInsertContent?.(metadata as M);
      }
    },
    [
      editorState,
      setEditorState,
      insertionType,
      usePlaintextMode,
      plaintextOptions,
      onInsertComplete,
      onInsertContent,
      transformTransaction,
    ]
  );
}
